import { toast } from "sonner";

/**
 * antd's `message` API, backed by sonner.
 *
 * An adapter rather than 40 hand-edits: antd's object form
 * (`message.success({ content, key, duration })`) differs from sonner's
 * `(content, { id, duration })` in three ways at once — argument shape, `key`
 * vs `id`, and **seconds vs milliseconds**. Translating that once here is safer
 * than doing it at every call site, and it keeps the loading→success handoff
 * (same `key`, replaces in place) working, which several flows rely on.
 *
 * Prefer importing `toast` from "sonner" directly in new code.
 */

type MessageArg =
  | React.ReactNode
  | {
      content: React.ReactNode;
      /** antd's dedupe/replace handle. Becomes sonner's `id`. */
      key?: string | number;
      /** **Seconds** in antd. Converted to ms below. 0 means "until dismissed". */
      duration?: number;
      onClose?: () => void;
    };

function normalize(arg: MessageArg, fallbackDuration?: number) {
  if (arg && typeof arg === "object" && "content" in arg) {
    const { content, key, duration, onClose } = arg as Exclude<
      MessageArg,
      React.ReactNode
    >;
    return {
      content,
      options: {
        ...(key !== undefined ? { id: key } : {}),
        // antd counts in seconds; 0 means "stay until dismissed", which sonner
        // spells as Infinity.
        duration:
          duration === undefined
            ? fallbackDuration
            : duration === 0
              ? Infinity
              : duration * 1000,
        ...(onClose ? { onDismiss: onClose, onAutoClose: onClose } : {}),
      },
    };
  }
  return { content: arg, options: { duration: fallbackDuration } };
}

export const message = {
  success(arg: MessageArg) {
    const { content, options } = normalize(arg);
    return toast.success(content as string, options);
  },
  error(arg: MessageArg) {
    const { content, options } = normalize(arg);
    return toast.error(content as string, options);
  },
  info(arg: MessageArg) {
    const { content, options } = normalize(arg);
    return toast.info(content as string, options);
  },
  warning(arg: MessageArg) {
    const { content, options } = normalize(arg);
    return toast.warning(content as string, options);
  },
  /** antd's loading toast stays up until replaced, so default to no timeout. */
  loading(arg: MessageArg) {
    const { content, options } = normalize(arg, Infinity);
    return toast.loading(content as string, options);
  },
  /** antd's `message.destroy(key?)`. No key dismisses everything. */
  destroy(key?: string | number) {
    if (key === undefined) toast.dismiss();
    else toast.dismiss(key);
  },
};

export default message;
